import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { SEARCH_RESULT_TYPES } from '../../constants'
import { SearchResult, SearchResultWrapLine } from '../../types'
import SearchResultItem from '../SearchResultItem'
import SearchIcons from '@/icons/SearchIcons'

interface SearchResultsListProps {
    query: string
    results: SearchResult[]
    recentSearches: string[]
    isLoading: boolean
    total: number
    onResultClick: (result: SearchResultWrapLine) => void
    onRecentSearchClick: (query: string) => void
}

const SearchResultsList = ({
    query,
    results,
    recentSearches,
    isLoading,
    total,
    onResultClick,
    onRecentSearchClick,
}: SearchResultsListProps) => {
    const { t } = useTranslation('search')

    // Group results by type
    const groupedResults = results.reduce(
        (acc, result) => {
            if (!acc[result.type]) {
                acc[result.type] = []
            }
            acc[result.type].push(result)
            return acc
        },
        {} as Record<string, SearchResult[]>
    )

    // Show recent searches when no query
    if (!query || query.length < 2) {
        return (
            <div className="p-4">
                {recentSearches.length > 0 ? (
                    <>
                        <Typogaphy className="text-sm font-medium text-gray-600 mb-3">
                            {t('recent-searches')}
                        </Typogaphy>
                        <div className="space-y-2">
                            {recentSearches.map((searchQuery, index) => (
                                <button
                                    key={index}
                                    onClick={() =>
                                        onRecentSearchClick(searchQuery)
                                    }
                                    className="flex items-center w-full text-left p-2 rounded hover:bg-gray-100 transition-colors group"
                                >
                                    <svg
                                        className="w-4 h-4 text-gray-400 mr-3 group-hover:text-gray-600 transition-colors"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                        {searchQuery}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100">
                            <Typogaphy className="text-xs text-gray-500 text-center">
                                {t('click-to-search-again')}
                            </Typogaphy>
                        </div>
                    </>
                ) : (
                    <div className="text-center text-gray-500 py-8">
                        <SearchIcons className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <Typogaphy className="text-base">
                            {t('search-placeholder')}
                        </Typogaphy>
                        <Typogaphy className="text-sm mt-2 text-gray-400">
                            {t('search-hint-message')}
                        </Typogaphy>
                    </div>
                )}
            </div>
        )
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loading.BarWave className="w-10 h-4" />
                <span className="ml-2 text-gray-500">{t('searching')}...</span>
            </div>
        )
    }

    // No results
    if (results.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                <svg
                    className="w-12 h-12 mx-auto mb-3 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.034 0-3.9.785-5.291 2.065M18 12a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                </svg>
                <Typogaphy className="text-base">
                    {t('no-results-found', { query })}
                </Typogaphy>
                <Typogaphy className="text-sm mt-1 text-gray-400">
                    {t('try-another-keyword-or-check-spelling')}
                </Typogaphy>

                {/* Show recent searches as suggestion when no results */}
                {recentSearches.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <Typogaphy className="text-sm font-medium text-gray-600 mb-3">
                            {t('try-recent-searches')}
                        </Typogaphy>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {recentSearches
                                .slice(0, 5)
                                .map((searchQuery, index) => (
                                    <button
                                        key={index}
                                        onClick={() =>
                                            onRecentSearchClick(searchQuery)
                                        }
                                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                                    >
                                        {searchQuery}
                                    </button>
                                ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="max-h-[400px] overflow-y-auto">
            {/* Results header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-[999]">
                <Typogaphy className="text-sm font-medium text-gray-600">
                    {t('found-results', { total, query })}
                </Typogaphy>
            </div>

            {/* Grouped results */}
            {Object.entries(groupedResults).map(([type, typeResults]) => {
                const typeConfig =
                    SEARCH_RESULT_TYPES[
                        type as keyof typeof SEARCH_RESULT_TYPES
                    ]

                return (
                    <div key={type}>
                        {/* Type header */}
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <Typogaphy className="text-sm font-medium text-gray-700">
                                    {t(typeConfig?.label)} ({typeResults.length}
                                    )
                                </Typogaphy>
                            </div>
                        </div>
                        <div>
                            {typeResults.map((result) => (
                                <SearchResultItem
                                    key={`${result.type}-${result.ref_id}`}
                                    result={result}
                                    query={query}
                                    onItemClick={onResultClick}
                                />
                            ))}
                        </div>
                    </div>
                )
            })}

            {/* Load more indicator */}
            {total > results.length && (
                <div className="p-3 text-center border-t border-gray-200">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        {t('view-more-results', {
                            count: total - results.length,
                        })}
                    </button>
                </div>
            )}
        </div>
    )
}

export default SearchResultsList
